import React, {useState} from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, LinearProgress, ListItemText, Menu,
    MenuItem, Paper, Stack, Switch, Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ApiService from '../../network/API';
import {confirmDialog} from '../../confirm';

const fmtTokens = (n) => (n >= 1000 ? `${Math.round(n / 100) / 10} тыс.` : String(n));
const STATUS_LABEL = {OK: 'можно проверить', TOO_LARGE: 'слишком большая работа', EMPTY: 'нет работы', DONE: 'уже проверен', ERROR: 'ошибка'};
const JOB_TITLE = {RUNNING: 'Идёт AI-проверка', DONE: 'AI-проверка завершена', CANCELED: 'AI-проверка прервана', FAILED: 'AI-проверка не удалась'};
const SCRIPT_JOB_TITLE = {RUNNING: 'Идёт автопроверка', DONE: 'Автопроверка завершена', CANCELED: 'Автопроверка прервана', FAILED: 'Автопроверка не удалась'};

const minutesAgo = (iso) => Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));

// Панель AI-проверки: запуск (по всем / по группе), перепроверка, настройки, и — главное — состояние фоновой задачи.
// Задача живёт на сервере: после перезагрузки страницы прогресс виден снова; пока она идёт, новый запуск недоступен,
// а прервать её можно кнопкой.
const GradingAiBar = ({problemId, status, onStatus, onStarted, groupId, groupName, nameOf, onDismissJob, jobDismissed, scripts = false}) => {
    const [menu, setMenu] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [estimate, setEstimate] = useState(null);
    const [estimating, setEstimating] = useState(false);

    const config = status?.config;
    const job = status?.job;
    const running = job?.state === 'RUNNING';
    const enabled = !!config?.enabled;
    const scope = groupName ? `группы «${groupName}»` : 'всех групп';

    const refresh = () => ApiService.getCriteriaAiStatus(problemId).then(onStatus).catch(() => {});

    const run = async (fn) => {
        setBusy(true);
        setError(null);
        try {
            await fn();
            await refresh();
            onStarted && onStarted();
        } catch (e) {
            setError(e.reason || e.message || 'Не удалось выполнить');
        } finally {
            setBusy(false);
        }
    };

    const who = scripts ? 'автопроверкой' : 'через AI';
    const startNew = async () => {
        const where = status?.autoPublish ? 'Баллы будут опубликованы автоматически.' : 'Баллы появятся черновиками.';
        if (!await confirmDialog(`Проверить ${who} ещё не проверенные работы ${scope}? ${where} Это фоновый процесс — страницу можно закрыть.`)) return;
        run(() => ApiService.runCriteriaAi(problemId, false, groupId));
    };

    const recheck = async () => {
        setMenu(null);
        let text;
        if (scripts) {
            text = `Перепроверить скриптами работы ${scope} заново (бесплатно). `;
        } else {
            let est;
            try {
                est = await ApiService.getCriteriaAiEstimate(problemId, groupId, true);
            } catch (e) {
                setError(e.reason || e.message || 'Не удалось оценить объём');
                return;
            }
            text = `Перепроверить работы ${scope}: ${est.checkable} через AI (≈ ${fmtTokens(est.totalEstimatedInputTokens)} токенов). `;
        }
        text += (status?.autoPublish ? 'Результаты, опубликованные автоматически, будут заменены — студенты сразу увидят новые баллы. ' : 'Новые баллы появятся черновиками. ')
            + 'Опубликованное вами вручную и ваши ручные баллы не изменятся.';
        if (!await confirmDialog(text)) return;
        run(() => ApiService.runCriteriaAi(problemId, true, groupId, true));
    };

    const cancel = async () => {
        if (!await confirmDialog('Прервать проверку? Студент, которого проверяют сейчас, доделается, остальные проверены не будут. Уже поставленные баллы сохранятся.', {confirmText: 'Прервать', danger: true})) return;
        run(() => ApiService.cancelCriteriaAi(problemId));
    };

    const openEstimate = async () => {
        setMenu(null);
        setEstimating(true);
        setError(null);
        try {
            setEstimate(await ApiService.getCriteriaAiEstimate(problemId, groupId));
        } catch (e) {
            setError(e.reason || e.message || 'Не удалось оценить объём');
        } finally {
            setEstimating(false);
        }
    };

    const toggleAuto = async () => {
        const next = !status?.autoPublish;
        if (next && !await confirmDialog('Включить автопубликацию? После AI-проверки студенты сразу увидят баллы и комментарии, а оценка лекции пересчитается. Баллы, выставленные вами вручную, и опубликованное вами AI не трогает.')) return;
        run(() => ApiService.setCriteriaAiAutoPublish(problemId, next));
    };

    const progress = job && job.total ? (job.done / job.total) * 100 : 0;
    const showJob = job && (running || !jobDismissed);

    return (
        <Box sx={{mb: 2}}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button variant="contained" color="secondary" startIcon={busy ? <CircularProgress size={16} color="inherit"/> : <AutoAwesomeIcon/>}
                        disabled={!enabled || running || busy} onClick={startNew}>
                    {groupName ? `Проверить группу ${groupName}` : (scripts ? 'Автопроверка: непроверенные' : 'AI: проверить непроверенных')}
                </Button>
                <Button variant="outlined" color="inherit" endIcon={<ArrowDropDownIcon/>} onClick={(e) => setMenu(e.currentTarget)}>Ещё</Button>
                {scripts ? (
                    <Chip size="small" variant="outlined" color="success" label="Скрипты критериев · бесплатно"/>
                ) : config && (
                    <Chip size="small" variant="outlined" color={enabled ? 'success' : 'default'}
                          label={enabled ? `AI включён${config.model ? ` · ${config.model}` : ''}` : 'AI не настроен'}/>
                )}
                {config?.monthlyTokenBudget > 0 && (
                    <Typography variant="caption" color="text.secondary">
                        токены за месяц: {fmtTokens(config.usedTokensThisMonth)} из {fmtTokens(config.monthlyTokenBudget)}
                    </Typography>
                )}
                <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
                    <MenuItem disabled={!enabled || running || busy} onClick={recheck}>
                        <ListItemText primary="Перепроверить всех" secondary="заново, включая уже проверенные AI"/>
                    </MenuItem>
                    {!scripts && (
                        <MenuItem onClick={openEstimate}>
                            <ListItemText primary="Оценить объём" secondary="сколько работ и токенов, без запуска"/>
                        </MenuItem>
                    )}
                    <Divider/>
                    <MenuItem disabled={!status || busy} onClick={() => { setMenu(null); toggleAuto(); }}>
                        <ListItemText primary="Автопубликация" secondary={status?.autoPublish ? 'включена: студенты видят баллы сразу' : 'выключена: результат — черновик'}/>
                        <Switch edge="end" size="small" checked={!!status?.autoPublish} tabIndex={-1} sx={{ml: 2}}/>
                    </MenuItem>
                </Menu>
            </Stack>

            {config && !enabled && (
                <Alert severity="info" sx={{mt: 1.5}}>На сервере не заданы LLM_BASE_URL, LLM_API_KEY и LLM_MODEL — запуск пока недоступен. Оценка объёма работает.</Alert>
            )}
            {error && <Alert severity="error" sx={{mt: 1.5}} onClose={() => setError(null)}>{error}</Alert>}

            {showJob && (
                <Paper variant="outlined" sx={{mt: 1.5, p: 2, borderRadius: 2, borderColor: running ? 'secondary.main' : 'divider'}}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
                        <Box sx={{minWidth: 0}}>
                            <Typography fontWeight={700}>
                                {running && <CircularProgress size={14} sx={{mr: 1}}/>}
                                {(scripts ? SCRIPT_JOB_TITLE : JOB_TITLE)[job.state] || job.state}: {job.done} из {job.total}
                            </Typography>
                            {running && job.currentUserId != null && (
                                <Typography variant="body2" color="text.secondary" noWrap>Сейчас: {nameOf(job.currentUserId)}</Typography>
                            )}
                        </Box>
                        {running
                            ? <Button color="error" size="small" startIcon={<StopCircleOutlinedIcon/>} onClick={cancel} disabled={busy}>Прервать</Button>
                            : <Button size="small" onClick={onDismissJob}>Скрыть</Button>}
                    </Stack>
                    <LinearProgress variant={running && job.done === 0 ? 'indeterminate' : 'determinate'} value={progress}
                                    color={job.errors > 0 ? 'warning' : 'secondary'} sx={{mt: 1, height: 6, borderRadius: 3}}/>
                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.75}}>
                        проверено {job.ok} · опубликовано {job.published} · пропущено {job.skipped} · ошибок {job.errors}
                    </Typography>
                    {job.stale && (
                        <Alert severity="warning" sx={{mt: 1}}>
                            Нет прогресса уже {minutesAgo(job.updatedAt)} мин — проверка, вероятно, зависла. Прервите её и запустите заново.
                        </Alert>
                    )}
                    {job.message && <Alert severity="error" sx={{mt: 1}}>{job.message}</Alert>}
                </Paper>
            )}

            <Dialog open={!!estimate || estimating} onClose={() => setEstimate(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Объём AI-проверки</DialogTitle>
                <DialogContent>
                    {!estimate ? <Box textAlign="center" py={3}><CircularProgress/></Box> : (
                        <>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{mb: 2}}>
                                <Chip label={`Работ: ${estimate.students}`}/>
                                <Chip color="success" variant="outlined" label={`К проверке: ${estimate.checkable}`}/>
                                <Chip variant="outlined" label={`Уже проверены: ${estimate.alreadyChecked}`}/>
                                <Chip color="warning" variant="outlined" label={`Слишком большие: ${estimate.tooLarge}`}/>
                                <Chip variant="outlined" label={`Пустые: ${estimate.empty}`}/>
                                <Chip color="primary" label={`≈ ${fmtTokens(estimate.totalEstimatedInputTokens)} токенов`}/>
                            </Stack>
                            <Stack spacing={0.5} sx={{maxHeight: 320, overflowY: 'auto'}}>
                                {estimate.items.map((i) => (
                                    <Stack key={i.userId} direction="row" justifyContent="space-between" gap={2}>
                                        <Typography variant="body2">{i.fullName}</Typography>
                                        <Typography variant="body2" color={i.status === 'OK' ? 'text.secondary' : 'warning.main'}>
                                            {i.status === 'OK' ? `≈ ${fmtTokens(i.estimatedTokens)}` : (i.message || STATUS_LABEL[i.status])}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </>
                    )}
                </DialogContent>
                <DialogActions><Button onClick={() => setEstimate(null)}>Закрыть</Button></DialogActions>
            </Dialog>
        </Box>
    );
};

export default GradingAiBar;
